import Fuse from 'fuse.js';


/**
 * @typedef {string} Route
 * A route name (see: /src/routes.js) associated with the result entity
 */

/**
 * @typedef {Array<number>} Indices
 * A tuple pair [start, end]
 */


/**
 * @typedef {object} Action
 * An action to perform on a result, provided by an item
 * @property {string} text Display text to show (in place of label)
 * @property {Function} do Callback, supplied self as arg
 */

/**
 * @typedef {object} Item
 * @property {string} name
 * @property {string} [label] The default label displayed (lazily) uses the route; Supplying a label overrides it.
 * @property {string|Array<string>} [tags] Tags for supplemental query-matching
 * @property {Route} route
 * @property {string} [id] Can be omitted for results without id like settings or reports pages
 * @property {object} [params] If result landing page uses params other than `id` (e.g.: reports use `:type`)
 * @property {string|Action} [alt] Alternate selection behavior to provide when user toggles a modifier
 */

/**
 * @typedef {object} Result
 * @property {Item} item
 * @property {object[]} matches
 * @property {Array<Indices>} matches.indices
 * @property {string} value Value matched by query
 * @property {string} key Name of property matched
 */

/* @ngInject */
export default function QuickfindService(
  DashboardInfoService,
  EquipmentInfoService,
  UserInfoService,
  GroupService,
  Organization,
  SiteService,
  UsersService,
) {
  const service = this;

  service.find = find;

  service.config = {

    /**
     * @constant
     * @type {number}
     * How many result items will be returned for a search
     * This number is generous to compensate for the service's inability to provide result-filtering
     */
    MAX_RESULTS_SIZE: 32,
    // TODO: dynamically adjust this somehow; based on device type?

    /**
     * @constant
     * @type {number}
     * Upper-bounds limit of search pattern
     */
    MAX_PATTERN_LENGTH: 32,
  };

  /**
   * @constant
   * @type {object}
   * {@link https://fusejs.io}
   */
  const fuseOptions = {
    // Continue search even if one result is perfect match
    findAllMatches: true,
    // Search individual words and the full string, computing the final score as a function of both
    tokenize: true,
    // Results will only include records that match all tokens
    matchAllTokens: true,
    // Whether to sort the result list, by score
    shouldSort: true,
    // Each record in the result set should include indices of the matched characters: indices: [start, end]
    includeMatches: true,
    includeScore: true,
    maxPatternLength: this.config.MAX_PATTERN_LENGTH,
    // Only the matches whose length exceeds this value will be returned
    minMatchCharLength: 2,
    // List of properties that will be searched
    // This supports nested properties, weighted search, searching in arrays of strings and objects
    keys: [
      'name',
      'id',
      'tags',
    ],
    // At what point does the match algorithm give up 0-1 where 0 is === and 1 is anything
    threshold: 0.5,
    // Disabled by tokenization:
    // Determines approximately where in the text is the pattern expected to be found
    // location: 0,
    // Determines how close the match must be to the fuzzy location
    // distance: 50,
  };

  /** @type {Array<Item>} */
  const bank = [];
  const list = new Fuse(bank, fuseOptions);


  (async function loadItems() {
    service.user = await UserInfoService.getCurrentUser();
    const userIsSuper = UserInfoService.isCurrentUserSuperAdmin();

    const { settings, admin, partner, superAdmin } = service.user;
    _.defaults(settings, {
      qf_dashboards: true,
      qf_equipment: true,
      qf_groups: true,
      qf_organizations: true,
      qf_sites: true,
      qf_users: false, // TODO: Re-enable
    });

    const groups = settings.qf_groups && await GroupService.getGroups();
    if (settings.qf_dashboards) {
      const dashboards = await DashboardInfoService.getAllDashboards();
      if (settings.qf_groups) {
        _.forEach(groups, (group) => {
          group.dashboards.forEach(({ id }) => {
            const match = _.find(dashboards, { id });
            _.defaults(match, { tags: [] });
            match.tags.push(group.name);
          });
        });
      }
      addToStore(sanitize(DashboardInfoService.dashboards, { route: 'dashboard' }));
    }

    if (settings.qf_groups) {
      if (admin) {
        addToStore(sanitize(groups, { route: 'admin.groups.group', label: 'Group' }));
      } else {
        // TODO: Pending implementation of landing page for group results for normal/admin users
      }

    }

    const USERS_ENABLED = false; // TODO: Disabled temporarily due to performance impact of retrieving user lists
    if (USERS_ENABLED && settings.qf_users) {
      if (userIsSuper) {
        addToStore(sanitize(await UsersService.getAllUsers(), { route: 'user log' }));
      // TODO: Tag users?
      } else {
        // TODO: Pending implementation of landing page for user results for normal/admin users
      }
    }

    if (settings.qf_equipment) {
      if (partner) {
        addToStore(sanitize(await EquipmentInfoService.getAllEquipments(), { route: 'equipment' }));
      // TODO: Tag equipment?
      } else {
        // TODO: Pending implementation of landing page for equipment results for normal/admin users
      }
    }

    if (settings.qf_sites) {
      if (admin) {
        addToStore(sanitize(await SiteService.get(), { route: 'admin.sites.site', label: 'Site' }));
      } else {
        // TODO: Pending implementation of landing page for site results for normal/admin users
      }
    }

    if (settings.qf_organizations) {
      if (partner) {
        const { array: orgs } = await Organization.model;
        addToStore(sanitize(orgs, { route: 'admin.organizations.org' }));
      } else {
        // TODO: Pending implementation of landing page for org results for normal/admin users
      }
    }


    const pages = [
      { route: 'user profile' },
      ...sanitize([
        { name: 'Diary Reports', params: { type: 'diary' } },
        { name: 'Event Reports', params: { type: 'events' } },
        { name: 'Flow Meter reports', params: { type: 'production-flowMeter' } },
        { name: 'Tank Reports', params: { type: 'production-tanks' } },
        { name: 'Water Disposal', params: { type: 'water-disposal' } },
        { name: 'LPR', params: { type: 'lpr' } },
      ], { route: 'reports' })
    ];
    // TODO: Programmatically retrieve these from `routes.js`
    if (partner) {
      pages.push(...[
        'equipment',
        'equipmentNotifications',
        'groups',
        'organizations',
        'rcuManagement',
        'sites',
        'userAccessLists',
        'users',
      ].map(route => ({ name: _.startCase(route), route: `admin.${route}` })));
    }
    if (superAdmin) {
      pages.push(...[
        'dataUsage',
        'equipment',
        'equipmentNotifications',
        'groups',
        'liveMonitoring',
        'monitoring',
        'organizations',
        'rcuManagement',
        'sites',
        'types',
        'userAccessLists',
        'users',
        'videoMonitoring',
      // Could potentially add nested states here like sites.new
      ].map(route => ({ name: _.startCase(route), route: `admin.${route}` })));
    }
    addToStore(pages.map(page => ({ name: _.startCase(page.route), ...page })));
  })();


  function find(term) {
    return _(list.search(term))
      // Unless sorted first, result.items without tags get buried by things that have them as tags
      .sortBy(({ item: { tags } }) => Boolean(tags))
      .slice(0, this.config.MAX_RESULTS_SIZE)
      .value();
  }

  function addToStore(items) {
    if (_.some(items, ({ name, route }) => !(name || route))) {
      throw new Error('Attempted to add result with missing properties to store');
    }
    bank.push(..._.castArray(items));
    refreshBank();
  }

  function refreshBank() {
    list.setCollection(bank);
  }

  /**
   * Removes unused properties from items in a collection and injects a given {Route} string
   *
   * @param {object | Array<object>} collection If supplied an Object will iterate over Object.values
   * @param {object} props
   * @param {Route} props.route
   * @param {string} [props.label]
   * @returns {Array<Item>} Sanitized collection
   * @private
   */
  function sanitize(collection, props) {
    if (_.isObject(collection)) {
      collection = _.values(collection);
    }
    if (!_.isArray(collection)) {
      throw new TypeError(`Unable to sanitize invalid entity type ${typeof collection} : ${collection}`);
    }
    return _.map(collection, entity => trim({ ...entity, ...props }));
  }

  /**
   * Transforms an entity, extracting only the props we need into a new object;
   *
   * @param {Item} entity
   * @returns {Item}
   * @private
   */
  function trim(entity) {
    return _.pick(entity, ['name', 'label', 'id', 'route', 'params', 'alt', 'tags']);
  }

}
